import { Lineicons, type LineiconsProps } from '@lineiconshq/react-lineicons';
import React, { ComponentType, ReactNode, useState } from 'react';
import { Loader2, LucideIcon } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { useExcelOperationLoadingSignal } from '@/lib/excel/operation-events';
import { useActionBarLoading } from '../action-bar-v2';
import { cn } from '@/lib/utils';

export type MenuButtonLineIconData = LineiconsProps['icon'];

const isLineIcon = (icon: unknown): icon is MenuButtonLineIconData =>
  typeof icon === 'object' && icon !== null && 'svg' in icon && 'viewBox' in icon;

export interface MenuItemProps {
  title: string;
  icon: ReactNode | LucideIcon | MenuButtonLineIconData;
  onClick?: () => unknown;
  group?: number;
  disabled?: boolean;
  loading?: boolean;
  loadingTitle?: string;
}

export interface MenuButtonV2Props {
  title?: string;
  items: MenuItemProps[];
  icon?: ReactNode | LucideIcon | MenuButtonLineIconData;
  className?: string;
}

export const MenuButtonV2: React.FC<MenuButtonV2Props> = ({ title, items, icon, className }) => {
  const [open, setOpen] = useState(false);
  const actionBarLoading = useActionBarLoading();
  const loadingExcelItem = items.find(
    item => item.loading && /excel|xuất|kết xuất/i.test(`${item.title} ${item.loadingTitle || ''}`)
  );

  useExcelOperationLoadingSignal(
    Boolean(loadingExcelItem),
    loadingExcelItem?.loadingTitle || 'Đang kết xuất dữ liệu...',
    `menu-button-${title || 'excel'}`
  );

  const groupedItems: { [key: number]: MenuItemProps[] } = {};
  items.forEach(item => {
    const group = item.group || 0;
    if (!groupedItems[group]) {
      groupedItems[group] = [];
    }
    groupedItems[group].push(item);
  });

  const groupKeys = Object.keys(groupedItems).map(Number).sort();

  const handleItemClick = async (item: MenuItemProps) => {
    if (!item.onClick || item.disabled || item.loading) {
      return;
    }

    const result = item.onClick();
    if (!result || typeof (result as Promise<unknown>).then !== 'function') {
      setOpen(false);
      return;
    }

    setOpen(false);
    const stopLoading = actionBarLoading?.beginLoading();
    try {
      await result;
    } finally {
      stopLoading?.();
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            'flex h-[var(--erp-command-button-height)] min-w-[56px] shrink-0 flex-col items-center justify-center rounded-[2.5px] bg-main px-3 py-0 font-semibold leading-none text-main-foreground transition duration-75 hover:bg-main-dark hover:shadow-sm',
            open && 'bg-main-dark shadow-sm',
            className
          )}
        >
          {icon ? (
            isLineIcon(icon) ? (
              <Lineicons aria-hidden='true' icon={icon} size={18} />
            ) : React.isValidElement(icon) ? (
              icon
            ) : (
              React.createElement(icon as ComponentType<{ className?: string }>, {
                className: 'size-[18px] text-main-foreground'
              })
            )
          ) : (
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='18'
              height='18'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <line x1='4' y1='6' x2='20' y2='6'></line>
              <line x1='4' y1='12' x2='20' y2='12'></line>
              <line x1='4' y1='18' x2='20' y2='18'></line>
            </svg>
          )}
          {title && <span className='mt-0.5 text-xs leading-3'>{title}</span>}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align='end'
        sideOffset={4}
        className='z-[99999] min-w-[220px] overflow-hidden rounded-lg border border-slate-200/80 bg-white p-1.5 shadow-none'
      >
        {groupKeys.map((group, groupIndex) => (
          <React.Fragment key={group}>
            {groupIndex > 0 && <DropdownMenuSeparator className='my-1.5 bg-slate-100' />}
            {groupedItems[group].map((item, itemIndex) => {
              const isLoading = item.loading;
              const isDisabled = item.disabled || isLoading;

              return (
                <DropdownMenuItem
                  key={`${group}-${itemIndex}`}
                  disabled={isDisabled}
                  onClick={() => void handleItemClick(item)}
                  className={cn(
                    'group relative flex cursor-pointer select-none items-center gap-2.5 rounded-md px-2.5 py-2 text-[13px] font-medium outline-none transition-all duration-150',
                    'text-slate-700 hover:bg-blue-50 hover:text-blue-700 focus:bg-blue-50 focus:text-blue-700',
                    isDisabled && 'cursor-not-allowed opacity-50 hover:bg-transparent hover:text-slate-700'
                  )}
                >
                  {/* Icon */}
                  <span
                    className={cn(
                      'flex h-[16px] w-[16px] shrink-0 items-center justify-center transition-colors duration-150',
                      isDisabled ? 'text-slate-400' : 'text-slate-500 group-hover:text-blue-600'
                    )}
                  >
                    {isLoading ? (
                      <Loader2 className='h-[16px] w-[16px] animate-spin' />
                    ) : isLineIcon(item.icon) ? (
                      <Lineicons aria-hidden='true' icon={item.icon} size={16} />
                    ) : React.isValidElement(item.icon) ? (
                      <span className='[&>*]:block [&>*]:size-[16px] [&>img]:size-[16px]'>{item.icon}</span>
                    ) : (
                      React.createElement(item.icon as ComponentType<{ className?: string }>, {
                        className: 'h-[16px] w-[16px]'
                      })
                    )}
                  </span>

                  {/* Label */}
                  <span className='flex-1 truncate'>
                    {isLoading && item.loadingTitle ? item.loadingTitle : item.title}
                  </span>

                  {/* Loading pulse dot */}
                  {isLoading && <span className='flex size-1.5 animate-pulse rounded-full bg-blue-400 opacity-75' />}
                </DropdownMenuItem>
              );
            })}
          </React.Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
export default MenuButtonV2;
